import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors, BRAND } from '../lib/theme';
import { useOptionLabel } from '../lib/useOptionLabel';
import { Icon } from './icons';

export interface SelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  dark: boolean;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  /** Accessible label (also used as the button's aria-label). */
  ariaLabel: string;
  /** Placeholder for the internal search box. Defaults to a generic "Suchen…". */
  searchPlaceholder?: string;
  /** Fixed button/panel width in px. Defaults to auto-sizing the button. */
  width?: number;
}

/**
 * A select replacement with an integrated text search inside the dropdown.
 *
 * Behaves like a native `<select>` (single value, controlled via value/onChange)
 * but renders a custom popover whose option list can be filtered by typing.
 * Used for the PB list filters (Status, Pflegegrad, Phase, Zugewiesen an,
 * Abwesenheit) so long option lists (e.g. many admins) stay navigable.
 */
export function SearchableSelect({
  dark,
  value,
  options: rawOptions,
  onChange,
  ariaLabel,
  searchPlaceholder,
  width,
}: SearchableSelectProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Options-Texte aus profile_options sind deutsch — nur die Anzeige übersetzen.
  const optLabel = useOptionLabel();
  const options = useMemo(
    () => rawOptions.map(o => ({ ...o, label: optLabel(o.label) })),
    [rawOptions, optLabel],
  );
  const selected = options.find(o => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Focus the search box when opening (no setState here — reset happens in the
  // toggle handler to avoid cascading renders from an effect).
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
    } else {
      setQuery('');
      setActiveIndex(0);
      setOpen(true);
    }
  };

  const commit = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, filtered.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) commit(opt.value);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', width: width ? width : undefined }}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggleOpen}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 8, padding: '7px 10px',
          background: t.inputBg, color: t.text, fontFamily: 'Outfit,sans-serif', cursor: 'pointer',
          width: width ? '100%' : undefined, minWidth: width ? undefined : 120, textAlign: 'left',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : ''}
        </span>
        <Icon.Chevron dir={open ? 'up' : 'down'} c={t.muted} />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={ariaLabel}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 30,
            minWidth: width ? '100%' : 200, width: width ? '100%' : 'max-content', maxWidth: 320,
            background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)', overflow: 'hidden',
          }}
        >
          <div style={{ padding: 8, borderBottom: `1px solid ${t.border}` }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: 8 }}><Icon.Search c={t.placeholder} size={13} /></span>
              <input
                ref={inputRef}
                value={query}
                onChange={e => { setQuery(e.target.value); setActiveIndex(0); }}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder ?? tr('common.action.search')}
                aria-label={searchPlaceholder ?? tr('common.action.search')}
                style={{
                  fontSize: 12, border: `1px solid ${t.border}`, borderRadius: 7,
                  padding: '6px 10px 6px 28px', width: '100%', background: t.inputBg, color: t.text,
                  fontFamily: 'Outfit,sans-serif', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto', padding: 4 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: 12, color: t.muted }}>
                {tr('common.search.empty')}
              </div>
            )}
            {filtered.map((o, i) => {
              const isSelected = o.value === value;
              const isActive = i === activeIndex;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => commit(o.value)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    width: '100%', textAlign: 'left', border: 'none', borderRadius: 7,
                    padding: '7px 10px', fontSize: 12, cursor: 'pointer',
                    fontFamily: 'Outfit,sans-serif',
                    background: isActive ? t.hover : 'transparent',
                    color: isSelected ? BRAND : t.text, fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                  {isSelected && <Icon.Check c={BRAND} size={12} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
